import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';
import GameCard from '../components/GameCard';
import useSEO from '../hooks/useSEO';

function Search() {
  useSEO({
    title: 'Search Games | Nightfall',
    description: 'Search and discover games on Nightfall',
    url: 'https://nightfallshop.com/search'
  });
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [tags, setTags] = useState([]);
  const [usedTags, setUsedTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ownedGameIds, setOwnedGameIds] = useState([]);
  const [creators, setCreators] = useState([]);
  const resultsCardRef = useRef(null);
  const tagsCardRef = useRef(null);

  const fetchOwnedGames = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/purchases/games`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const ownedIds = data.games.map(g => g.gameId);
        setOwnedGameIds(ownedIds);
      }
    } catch (err) {
      console.error('Error fetching owned games:', err);
    }
  }, [token]);

  const filterGames = useCallback(() => {
    if (!allGames.length) {
      setFilteredGames([]);
      return;
    }

    let filtered = [...allGames];

    // Filter by selected tags first (games must have ALL selected tags)
    if (selectedTags.length > 0) {
      filtered = filtered.filter(game => 
        game.tagIds && selectedTags.every(selectedTag => 
          game.tagIds.includes(selectedTag.id)
        )
      );
    }

    // Filter by search query with prioritization
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      
      // Create a mapping of creatorId to creator name
      const creatorMap = new Map();
      creators.forEach(creator => {
        creatorMap.set(creator.id, creator.name.toLowerCase());
      });

      // Separate games into priority groups
      const titleMatches = [];
      const creatorMatches = [];
      const seenGameIds = new Set();

      filtered.forEach(game => {
        const gameTitleLower = game.title.toLowerCase();
        const creatorName = game.creatorId ? creatorMap.get(game.creatorId) : '';
        
        // Check if title starts with query (highest priority)
        if (gameTitleLower.startsWith(query)) {
          if (!seenGameIds.has(game.id)) {
            titleMatches.push(game);
            seenGameIds.add(game.id);
          }
        }
        // Check if creator name starts with query (second priority)
        else if (creatorName && creatorName.startsWith(query)) {
          if (!seenGameIds.has(game.id)) {
            creatorMatches.push(game);
            seenGameIds.add(game.id);
          }
        }
      });

      // Combine results with title matches first, then creator matches
      filtered = [...titleMatches, ...creatorMatches];
    }

    setFilteredGames(filtered);
  }, [allGames, selectedTags, searchQuery, creators]);

  useEffect(() => {
    fetchTags();
    fetchGames();
    fetchCreators();
    if (isAuthenticated && token) {
      fetchOwnedGames();
    }
  }, [isAuthenticated, token, fetchOwnedGames]);


  // Filter tags to only show those used by games
  useEffect(() => {
    if (allGames.length > 0 && tags.length > 0) {
      // Get all unique tag IDs used by games
      const usedTagIds = new Set();
      allGames.forEach(game => {
        if (game.tagIds && Array.isArray(game.tagIds)) {
          game.tagIds.forEach(tagId => usedTagIds.add(tagId));
        }
      });
      
      // Filter tags to only include those that are used
      const filtered = tags.filter(tag => usedTagIds.has(tag.id));
      setUsedTags(filtered);
    }
  }, [allGames, tags]);

  useEffect(() => {
    if (searchQuery.trim() || selectedTags.length > 0) {
      filterGames();
    } else {
      setFilteredGames([]);
    }
  }, [searchQuery, selectedTags, allGames, creators, filterGames]);

  // Match tags card height to results card height
  useEffect(() => {
    const matchHeights = () => {
      if (resultsCardRef.current && tagsCardRef.current) {
        const resultsHeight = resultsCardRef.current.offsetHeight;
        tagsCardRef.current.style.height = `${resultsHeight}px`;
      }
    };

    // Use setTimeout to ensure DOM is updated
    const timeoutId = setTimeout(matchHeights, 0);
    window.addEventListener('resize', matchHeights);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', matchHeights);
    };
  }, [filteredGames, searchQuery, selectedTags]);

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/tags`);
      if (response.ok) {
        const data = await response.json();
        setTags(data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/games`);
      if (response.ok) {
        const data = await response.json();
        setAllGames(data);
      }
    } catch (error) {
      console.error('Error fetching games:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreators = async () => {
    try {
      const response = await fetch(`${API_URL}/creators`);
      if (response.ok) {
        const data = await response.json();
        setCreators(data);
      }
    } catch (error) {
      console.error('Error fetching creators:', error);
    }
  };

  const handleTagClick = (tag) => {
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t.id === tag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== tag.id);
      } else {
        return [...prev, tag];
      }
    });
  };

  const hasActiveSearch = searchQuery.trim() || selectedTags.length > 0;

  return (
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden" style={{ height: 'calc(100vh - 5.75rem - 0.5rem - 1.5rem)' }}>
      <style>{`
        .search-align {
          margin-top: calc(5.75rem + 0.5rem - 6rem);
        }
        @media (min-width: 768px) {
          .search-align {
            margin-top: calc(5.75rem + 1.25rem - 8rem);
          }
        }
      `}</style>

      <div className="search-align w-full mb-6">
        <h2 className="font-semibold text-3xl lg:text-4xl text-white">Search</h2>
      </div>

      <div className="w-full flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="flex flex-col lg:flex-row gap-6 flex-1 overflow-hidden min-h-0">
          {/* Left Side - Search Bar and Tags */}
          <div className="lg:w-1/3 flex flex-col gap-6 min-h-0">
            {/* Search Bar Component */}
            <div className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8 flex-shrink-0">
              <h3 className="font-semibold text-lg lg:text-xl text-white mb-4">Search Games</h3>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search games..."
                  className="w-full px-4 py-3 bg-dark-surface/50 border border-dark-border rounded-2xl text-white placeholder-white/40 focus:outline-none focus:border-accent-blue/50 transition-all duration-200"
                />
                <svg 
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Tags Component */}
            <div ref={tagsCardRef} className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8 flex-1 flex flex-col min-h-0">
              <h3 className="font-semibold text-lg lg:text-xl text-white mb-4 flex-shrink-0">Tags</h3>
              <div 
                className="flex-1 overflow-y-auto tags-scrollbar"
              >
                {usedTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {usedTags.map(tag => {
                      const isSelected = selectedTags.some(t => t.id === tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => handleTagClick(tag)}
                          className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-200"
                          style={{ 
                            backgroundColor: isSelected 
                              ? `${tag.color}40` 
                              : `${tag.color}08`, 
                            border: `1px solid ${isSelected ? tag.color : `${tag.color}30`}`, 
                            color: isSelected ? tag.color : `${tag.color}60`
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-white/40 text-sm">No tags available</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Side - Results Component */}
          <div className="lg:w-2/3 lg:flex-1 overflow-hidden flex flex-col min-h-0">
            <div ref={resultsCardRef} className="bg-gradient-to-br from-dark-card to-dark-surface border border-dark-border rounded-3xl p-6 lg:p-8 flex-1 flex flex-col min-h-0">
              {!hasActiveSearch ? (
                <div className="flex flex-col items-center justify-center flex-1">
                  <svg 
                    className="w-16 h-16 md:w-20 md:h-20 text-white/20 mb-4" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-white/60 text-lg md:text-xl text-center">Search for games or select a tag to get started</p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center min-h-[400px]">
                  <p className="text-white/60 text-lg">Loading...</p>
                </div>
              ) : filteredGames.length > 0 ? (
                <>
                  <h3 className="font-semibold text-lg lg:text-xl text-white mb-6 flex-shrink-0">
                    Search Results
                  </h3>
                  <div className="flex-1 overflow-y-auto tags-scrollbar">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
                      {filteredGames.map(game => (
                        <GameCard 
                          key={game.id} 
                          game={game} 
                          isOwned={ownedGameIds.includes(game.id)} 
                        />
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center min-h-[400px]">
                  <div className="text-center">
                    <p className="text-white/60 text-lg md:text-xl mb-2">No games found</p>
                    <p className="text-white/40 text-sm md:text-base">Try a different search term or tag</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Search;

